import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import Loading from '../../../components/shared/Loading/Loading';
import ShareButtons from '../../../components/shared/ShareButtons/ShareButtons';
import { empresaShareUrl, empresaShareText } from '../../../utils/share';
import {
  PageWrap,
  Hero,
  HeroInner,
  Logo,
  HeroText,
  Section,
  Grid,
  Card,
  Notice,
  EmptyActivities,
} from './EmpresaPublic.styles';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop';

// Perfil público: GET /api/public/empresas/<slug> (sin autenticación).
// Es la página a la que redirige /share/empresa/<slug>.
const EmpresaPublic = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', empresa: null, activities: [] });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', empresa: null, activities: [] });

    const load = async () => {
      try {
        const response = await fetch(`/api/public/empresas/${encodeURIComponent(slug)}`);
        if (response.status === 404) {
          if (!cancelled) setState({ status: 'notfound', empresa: null, activities: [] });
          return;
        }
        const data = await response.json();
        if (cancelled) return;
        if (response.ok && data.success) {
          setState({
            status: 'ok',
            empresa: data.empresa,
            activities: data.activities || [],
          });
        } else {
          setState({ status: 'error', empresa: null, activities: [] });
        }
      } catch (error) {
        console.error('Error loading empresa profile:', error);
        if (!cancelled) setState({ status: 'error', empresa: null, activities: [] });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const { status, empresa, activities } = state;

  useEffect(() => {
    if (status !== 'ok' || !empresa) return undefined;
    const previous = document.title;
    document.title = `${empresa.businessName} | BokerHub`;
    return () => {
      document.title = previous;
    };
  }, [status, empresa]);

  if (status === 'loading') {
    return (
      <Layout>
        <Loading text="Cargando perfil..." />
      </Layout>
    );
  }

  if (status === 'notfound') {
    return (
      <Layout>
        <Notice>
          <div className="emoji" aria-hidden="true">🧭</div>
          <h1>No encontramos esta empresa</h1>
          <p>
            Puede que el enlace esté incompleto o que la empresa ya no tenga un
            perfil público. Mientras tanto, descubre otras experiencias.
          </p>
          <div className="buttons">
            <Button onClick={() => navigate('/search')}>Explorar actividades</Button>
            <Button variant="secondary" onClick={() => navigate('/')}>Ir al inicio</Button>
          </div>
        </Notice>
      </Layout>
    );
  }

  if (status === 'error') {
    return (
      <Layout>
        <Notice>
          <div className="emoji" aria-hidden="true">⚠️</div>
          <h1>No pudimos cargar el perfil</h1>
          <p>Hubo un problema de conexión. Inténtalo de nuevo en unos segundos.</p>
          <div className="buttons">
            <Button onClick={() => window.location.reload()}>Reintentar</Button>
            <Button variant="secondary" onClick={() => navigate('/')}>Ir al inicio</Button>
          </div>
        </Notice>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageWrap>
        <Hero>
          <HeroInner>
            <Logo>
              {empresa.logo ? (
                <img src={empresa.logo} alt={`Logo de ${empresa.businessName}`} />
              ) : (
                <span aria-hidden="true">
                  {(empresa.businessName || '?').trim().charAt(0).toUpperCase()}
                </span>
              )}
            </Logo>
            <HeroText>
              <h1>{empresa.businessName}</h1>
              {empresa.city && <p className="city">📍 {empresa.city}</p>}
              {empresa.description && <p className="description">{empresa.description}</p>}
              <div className="share">
                <ShareButtons
                  label="Compartir perfil"
                  title={empresa.businessName}
                  text={empresaShareText(empresa.businessName)}
                  url={empresaShareUrl(empresa.slug || slug)}
                />
              </div>
            </HeroText>
          </HeroInner>
        </Hero>

        <Section>
          <h2>Actividades</h2>
          {activities.length > 0 ? (
            <Grid>
              {activities.map((activity) => (
                <Card key={activity.id} href={`/activity/${activity.id}`}
                  onClick={(e) => {
                    // Navegación dentro de la SPA salvo clic con tecla modificadora.
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    navigate(`/activity/${activity.id}`);
                  }}
                >
                  <img src={activity.images?.[0] || FALLBACK_IMAGE} alt={activity.title} loading="lazy" />
                  <div className="body">
                    <h3>{activity.title}</h3>
                    {activity.description && <p>{activity.description}</p>}
                    <div className="meta">
                      <span className="price">
                        ${typeof activity.price === 'number' ? activity.price.toLocaleString() : activity.price}
                      </span>
                      {activity.duration && <span>⏱ {activity.duration}</span>}
                    </div>
                    <div className="meta">
                      {activity.location && <span>📍 {activity.location}</span>}
                      {activity.reviewCount > 0 && (
                        <span>⭐ {activity.rating} ({activity.reviewCount})</span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </Grid>
          ) : (
            <EmptyActivities>
              <p>Esta empresa aún no tiene actividades publicadas. ¡Vuelve pronto!</p>
            </EmptyActivities>
          )}
        </Section>
      </PageWrap>
    </Layout>
  );
};

export default EmpresaPublic;
